import React, { useEffect, useRef, useState } from "react";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import CallEndIcon from "@mui/icons-material/CallEnd";
import PersonIcon from "@mui/icons-material/Person";
import { useCall } from "../../../../../../context/temp";

export default function ActiveCallModal() {
  const {
    activeCall,
    callStatus,
    localStream,
    remoteStream,
    isMuted,
    isVideoOff,
    endCall,
    toggleMute,
    toggleVideo,
  } = useCall();

  const [callDuration, setCallDuration] = useState(0);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);

  const BASE_DOWNLOAD_URL = process.env.REACT_APP_API_BASE_URL_DOWNLOAD;

  // Update call duration
  useEffect(() => {
    if (callStatus !== "connected" || !activeCall) return;

    const interval = setInterval(() => {
      const duration = Math.floor((Date.now() - activeCall.startTime) / 1000);
      setCallDuration(duration);
    }, 1000);

    return () => clearInterval(interval);
  }, [callStatus, activeCall]);

  // ✅ IMPROVED: Set up local video stream
  useEffect(() => {
    const setupLocalVideo = async () => {
      if (localVideoRef.current && localStream) {
        try {
          // Set the stream
          localVideoRef.current.srcObject = localStream;

          console.log("🎥 [ActiveCall] Setting local stream");
          console.log("📹 Video tracks:", localStream.getVideoTracks().length);
          console.log("🎤 Audio tracks:", localStream.getAudioTracks().length);

          // Log track states
          localStream.getTracks().forEach((track) => {
            console.log(
              `Track ${track.kind}: enabled=${track.enabled}, muted=${track.muted}, readyState=${track.readyState}`
            );
          });

          // Ensure video element is ready and playing
          await localVideoRef.current.play();
          console.log("✅ [ActiveCall] Local video playing");
        } catch (err) {
          console.error("❌ [ActiveCall] Failed to set local stream:", err);
        }
      } else {
        console.log("⚠️ [ActiveCall] Missing localVideoRef or localStream", {
          hasRef: !!localVideoRef.current,
          hasStream: !!localStream,
          streamTracks: localStream?.getTracks().length || 0,
        });
      }
    };

    setupLocalVideo();
  }, [localStream, isVideoOff]);

  // Set up remote video/audio stream with autoplay
  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;

      // Force play for audio
      remoteVideoRef.current
        .play()
        .then(() => {
          console.log("✅ [ActiveCall] Remote stream playing");
        })
        .catch((err) => {
          console.error("❌ [ActiveCall] Failed to play remote stream:", err);
          // Retry after a delay
          setTimeout(() => {
            remoteVideoRef.current?.play().catch(console.error);
          }, 500);
        });

      // Log tracks
      remoteStream.getTracks().forEach((track) => {
        console.log(
          `🎵 [ActiveCall] Remote track: ${track.kind}, enabled: ${track.enabled}, muted: ${track.muted}`
        );
      });
    }
  }, [remoteStream]);

  if (!activeCall) return null;

  const { otherUser, type } = activeCall;
  const otherUserName = otherUser.username || "Unknown User";
  const otherUserAvatar = otherUser.avatar
    ? `${BASE_DOWNLOAD_URL}${otherUser.avatar}`
    : null;

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const getStatusText = () => {
    switch (callStatus) {
      case "ringing":
        return "Ringing...";
      case "connecting":
        return "Connecting...";
      case "connected":
        return formatDuration(callDuration);
      default:
        return "";
    }
  };

  // ✅ FIXED: Check if we should show local video
  const shouldShowLocalVideo = localStream && type === "video" && !isVideoOff;

  return (
    <div className="fixed inset-0 z-50 bg-gray-900 flex flex-col">
      {/* Video Container */}
      <div className="flex-1 relative">
        {type === "video" ? (
          <>
            {/* Remote Video (Full Screen) */}
            <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
              {remoteStream ? (
                <video
                  ref={remoteVideoRef}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                  // CRITICAL: Don't mute remote video - it contains audio!
                  muted={false}
                />
              ) : (
                <div className="flex flex-col items-center">
                  <div className="w-32 h-32 rounded-full bg-gray-700 flex items-center justify-center mb-4">
                    {otherUserAvatar ? (
                      <img
                        src={otherUserAvatar}
                        alt={otherUserName}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <PersonIcon className="text-gray-400 text-6xl" />
                    )}
                  </div>
                  <p className="text-white text-lg">{otherUserName}</p>
                  <p className="text-gray-400 text-sm mt-2">
                    {getStatusText()}
                  </p>
                </div>
              )}
            </div>

            {/* ✅ FIXED: Local Video (Picture-in-Picture) */}
            <div className="absolute top-4 right-4 w-32 h-40 bg-gray-800 rounded-lg overflow-hidden shadow-2xl border-2 border-gray-600">
              {localStream && type === "video" && !isVideoOff ? (
                <video
                  ref={localVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover transform scale-x-[-1]"
                  onLoadedMetadata={() => {
                    console.log("✅ Local video metadata loaded");
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-700">
                  <VideocamOffIcon className="text-gray-400 text-4xl" />
                </div>
              )}
            </div>

            {/* Debug info (remove in production) */}
            {process.env.NODE_ENV === "development" && (
              <div className="absolute bottom-20 left-4 bg-black bg-opacity-70 text-white text-xs p-2 rounded">
                <div>Local Stream: {localStream ? "✅" : "❌"}</div>
                <div>
                  Video Tracks: {localStream?.getVideoTracks().length || 0}
                </div>
                <div>Video Off: {isVideoOff ? "✅" : "❌"}</div>
                <div>Should Show: {shouldShowLocalVideo ? "✅" : "❌"}</div>
              </div>
            )}
          </>
        ) : (
          // Voice Call UI - Still needs audio playback
          <div className="absolute inset-0 bg-gradient-to-b from-green-600 to-green-800 flex flex-col items-center justify-center">
            {/* Hidden audio element for voice calls */}
            {remoteStream && (
              <audio
                ref={remoteVideoRef}
                autoPlay
                playsInline
                style={{ display: "none" }}
              />
            )}

            <div className="w-40 h-40 rounded-full bg-white shadow-2xl overflow-hidden mb-6">
              {otherUserAvatar ? (
                <img
                  src={otherUserAvatar}
                  alt={otherUserName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-300 text-gray-700 text-6xl font-bold">
                  {otherUserName[0]?.toUpperCase()}
                </div>
              )}
            </div>
            <h2 className="text-white text-3xl font-semibold mb-2">
              {otherUserName}
            </h2>
            <p className="text-green-100 text-lg">{getStatusText()}</p>
          </div>
        )}

        {/* Call Status Overlay (for video calls) */}
        {type === "video" && (
          <div className="absolute top-4 left-4 bg-black bg-opacity-50 rounded-lg px-4 py-2">
            <p className="text-white text-lg font-semibold">{otherUserName}</p>
            <p className="text-gray-300 text-sm">{getStatusText()}</p>
          </div>
        )}
      </div>

      {/* Control Panel */}
      <div className="bg-gray-900 py-8 px-6">
        <div className="flex justify-center items-center gap-8 max-w-md mx-auto">
          {/* Mute Button */}
          <button
            onClick={toggleMute}
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 ${
              isMuted
                ? "bg-red-500 hover:bg-red-600"
                : "bg-gray-700 hover:bg-gray-600"
            }`}
            aria-label={isMuted ? "Unmute" : "Mute"}
          >
            {isMuted ? (
              <MicOffIcon className="text-white text-2xl" />
            ) : (
              <MicIcon className="text-white text-2xl" />
            )}
          </button>

          {/* End Call Button */}
          <button
            onClick={() => endCall("user_hangup")}
            className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 shadow-lg"
            aria-label="End call"
          >
            <CallEndIcon className="text-white text-3xl" />
          </button>

          {/* Video Toggle Button (only for video calls) */}
          {type === "video" && (
            <button
              onClick={toggleVideo}
              className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 ${
                isVideoOff
                  ? "bg-red-500 hover:bg-red-600"
                  : "bg-gray-700 hover:bg-gray-600"
              }`}
              aria-label={isVideoOff ? "Turn on camera" : "Turn off camera"}
            >
              {isVideoOff ? (
                <VideocamOffIcon className="text-white text-2xl" />
              ) : (
                <VideocamIcon className="text-white text-2xl" />
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
